import { cn } from '../utils/cn';
import {
  LayoutDashboard,
  Home,
  Users,
  Stethoscope,
  CalendarDays,
  ClipboardList,
  Video,
  Coins,
  BarChart3,
  Settings,
  MessageCircle,
  Gift,
  Megaphone,
  History,
  BadgePercent,
  CreditCard,
  UsersRound,
  Camera,
  Clock3,
  UserPlus,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n/LanguageContext';
import { TranslationKey } from '../i18n/translations';
import type { LucideIcon } from 'lucide-react';
import { useMessageUnread } from '../hooks/useMessageUnread';
import { usePatientTerm } from '../hooks/useClinicType';
import { useApiClient } from '../lib/apiClient';
import { NAVIGATION_MENU_PERMISSION_GROUPS, permissionCodeMap } from '../lib/permissionCatalog';
import { filterMvpNavItems } from '../config/mvpVisibility';

type Props = {
  collapsed: boolean;
  onToggle: () => void;
};

const baseNavItems: Array<{ label: string; icon: LucideIcon; path: string; i18nKey: TranslationKey; adminOnly?: boolean }> = [
  { label: 'Home', icon: Home, path: '/dashboard', i18nKey: 'sidebar.dashboard' },
  { label: 'Reports', icon: BarChart3, path: '/reports/operations', i18nKey: 'sidebar.reports' },
  { label: 'Appointments', icon: CalendarDays, path: '/appointments', i18nKey: 'sidebar.appointments' },
  { label: 'Command Center', icon: LayoutDashboard, path: '/command-center', i18nKey: 'sidebar.commandCenter', adminOnly: true },
  { label: 'Patients', icon: Users, path: '/patients', i18nKey: 'sidebar.patients' },
  { label: 'Messages', icon: MessageCircle, path: '/messages', i18nKey: 'sidebar.messages' },
  { label: 'Walk-In Triage', icon: Stethoscope, path: '/core-clinical/walk-in-urgent-care', i18nKey: 'sidebar.walkInUrgentCare' },
  { label: 'Telemedicine', icon: Video, path: '/telemedicine', i18nKey: 'sidebar.telemedicine' },
  { label: 'Billing', icon: Coins, path: '/billing', i18nKey: 'sidebar.billing' },
  { label: 'Offers & Packages', icon: Gift, path: '/offers', i18nKey: 'sidebar.offersPackages' },
  { label: 'Loyalty', icon: BadgePercent, path: '/loyalty', i18nKey: 'sidebar.loyalty' },
  { label: 'POS', icon: CreditCard, path: '/pos', i18nKey: 'sidebar.pos' },
  { label: 'Staff Operations', icon: UsersRound, path: '/staff-operations', i18nKey: 'sidebar.staffOperations' },
  { label: 'Timesheets', icon: Clock3, path: '/timesheets', i18nKey: 'sidebar.timesheets' },
  { label: 'Mobile Capture', icon: Camera, path: '/mobile-capture', i18nKey: 'sidebar.mobileCapture' },
  { label: 'Leads', icon: UserPlus, path: '/leads', i18nKey: 'sidebar.leads' },
  { label: 'Inventory', icon: ClipboardList, path: '/inventory', i18nKey: 'sidebar.inventory' },
  { label: 'Advanced Inventory', icon: ClipboardList, path: '/inventory/advanced', i18nKey: 'sidebar.advancedInventory' },
  { label: 'Marketing', icon: Megaphone, path: '/marketing', i18nKey: 'sidebar.marketing' },
  { label: 'Audit', icon: History, path: '/audit/activity', i18nKey: 'sidebar.audit' },
  { label: 'Settings', icon: Settings, path: '/settings', i18nKey: 'sidebar.settings' },
];

const navPermissionCodes = permissionCodeMap(NAVIGATION_MENU_PERMISSION_GROUPS);
const mvpNavItems = filterMvpNavItems(baseNavItems);

export default function Sidebar({ collapsed, onToggle }: Props) {
  const location = useLocation();
  const { t } = useI18n();
  const { fetchJson } = useApiClient();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const { count: unreadCount } = useMessageUnread(30000);
  const patientTerm = usePatientTerm();
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const me = await fetchJson<{ role?: string; roles?: string[]; permissionCodes?: string[] }>('/users/me');
        const roles = (Array.isArray(me?.roles) ? me.roles : [me?.role]).map((entry) => String(entry || '').toUpperCase()).filter(Boolean);
        const permissions = Array.isArray(me?.permissionCodes) ? me.permissionCodes.map((entry) => String(entry || '').trim()).filter(Boolean) : [];
        if (active) setUserRoles(roles);
        if (active) setUserPermissions(permissions);
      } catch {
        if (active) setUserRoles([]);
        if (active) setUserPermissions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson]);

  const navItems = useMemo(() => {
    const roleSet = new Set(userRoles);
    const permissionSet = new Set(userPermissions);
    const hasPermissionCatalog = permissionSet.size > 0;
    const isAdmin = roleSet.has('ADMIN');
    const isClinician = roleSet.has('DOCTOR') || roleSet.has('NURSE') || roleSet.has('RECEPTIONIST');

    const visibleByPermission = (item: (typeof baseNavItems)[number]) => {
      const codes = navPermissionCodes[item.label] || [];
      if (!codes.length) return true;
      return codes.some((code) => permissionSet.has(code));
    };

    if (isAdmin) {
      return mvpNavItems;
    }
    if (hasPermissionCatalog) {
      return mvpNavItems.filter((item) => visibleByPermission(item));
    }
    return mvpNavItems.filter((item) => !item.adminOnly);
  }, [userPermissions, userRoles]);

  const localizedNavItems = navItems.map((item) => ({
    ...item,
    // Clinic-type-aware label: med spas / wellness see "Clients", clinical verticals "Patients".
    label: item.path === '/patients' ? patientTerm.plural : t(item.i18nKey, item.label),
    badgeCount: item.path === '/messages' ? unreadCount : 0,
  }));
  return (
    <aside
      className={cn(
        'neo-sidebar bg-white border-r border-slate-100 shadow-soft relative transition-all duration-300 ease-in-out',
        collapsed ? 'w-20' : 'w-64',
      )}
    >
      <div className={cn('flex items-center py-4', collapsed ? 'justify-center px-0' : 'justify-between px-4')}>
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2')}>
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              fontSize: '1.3rem',
              fontWeight: 700,
              fontFamily: 'Inter, sans-serif',
              marginTop: collapsed ? '0' : '-5px',
            }}
            className="h-9 w-9 rounded-xl bg-ink text-white flex items-center justify-center font-semibold cursor-pointer"
          >
            e
          </button>
          {!collapsed && (
            <div>

              <p
                className="flex items-center gap-1"
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  color: 'var(--text)',
                  lineHeight: 1.1,
                }}
              >
                <span>elen1</span>
                <span className="neo-sidebar-brand-dot" style={{ color: '#3b82f6' }}>.</span>
              </p>
            </div>
          )}
        </div>
      </div>
      <nav className="px-2 space-y-1">
        {localizedNavItems.map((item) => {
          const Icon = item.icon;
          const activationPrefixes = item.path === '/reports/operations' ? ['/reports'] : [item.path];
          const active = activationPrefixes.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
          const hoverClass = active ? 'hover:bg-ink' : 'hover:bg-slate-50';
          const badgeText = item.badgeCount && item.badgeCount > 99 ? '99+' : item.badgeCount;
          const showBadge = Boolean(item.badgeCount);
          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition relative',
                hoverClass,
                active ? 'neo-sidebar-active bg-ink text-white' : 'text-slate-600',
                collapsed ? 'justify-center' : 'justify-start',
              )}
            >
              <Icon size={18} />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {showBadge ? (
                <span
                  className={cn(
                    'inline-flex min-w-[20px] h-5 items-center justify-center rounded-full text-[11px] font-semibold',
                    active ? 'bg-white text-ink' : 'bg-ink text-white',
                    collapsed ? 'absolute top-1 right-2' : '',
                  )}
                >
                  {badgeText}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
